"use client";

import { useUserTeamForTournament } from "@/hooks";
import { getSportIcon } from "@/lib/sports/icons";
import { Location, Organizer, Tournament } from "@/types";
import { Icon } from "@iconify/react";
import { formatDistanceToNowStrict } from "date-fns";
import { enUS, fi } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

interface TournamentCardFantasyProps {
  tournament: Tournament;
  organizer: Organizer;
  locations?: Location[];
  teamCount?: number;
  playerCount?: number;
  matchCount?: number;
  freePlayerCount?: number;
}

interface StatusChip {
  variant: "signin" | "attending" | "active" | "ended" | "starting";
  label: string;
}

export function TournamentCardFantasy({
  tournament,
  organizer,
  locations,
  teamCount = 0,
  playerCount = 0,
  freePlayerCount = 0,
}: TournamentCardFantasyProps) {
  const t = useTranslations();
  const locale = useLocale();
  const dateLocale = locale === "fi" ? fi : enUS;
  const { data: userTeam } = useUserTeamForTournament(tournament.id);
  const isAttending = !!userTeam;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const now = mounted ? new Date() : null;
  const signInStartsAt = tournament.signInStartsAt
    ? new Date(tournament.signInStartsAt)
    : null;
  const signInEndsAt = tournament.signInEndsAt
    ? new Date(tournament.signInEndsAt)
    : null;

  const isSignInOpen = now
    ? (!signInStartsAt || now >= signInStartsAt) &&
      (!signInEndsAt || now <= signInEndsAt)
    : false;

  const tournamentEnded = now ? now > new Date(tournament.endsAt) : false;
  const isTournamentActive = now
    ? now >= new Date(tournament.startsAt) && !tournamentEnded
    : false;

  const status: StatusChip | null = !mounted
    ? null
    : tournamentEnded
      ? { variant: "ended", label: t("tournaments.ended") }
      : isAttending
        ? { variant: "attending", label: t("tournaments.attending") }
        : isSignInOpen
          ? {
              variant: "signin",
              label: t("tournaments.signInOpen", {
                date: signInEndsAt
                  ? formatInTimeZone(
                      signInEndsAt,
                      "Europe/Helsinki",
                      "d.M. HH:mm",
                    )
                  : "",
              }),
            }
          : isTournamentActive
            ? { variant: "active", label: t("tournaments.active") }
            : {
                variant: "starting",
                label: t("tournaments.startsIn", {
                  duration: formatDistanceToNowStrict(
                    new Date(tournament.startsAt),
                    { locale: dateLocale, addSuffix: true },
                  ),
                }),
              };

  const locationLabel =
    locations && locations.length > 0
      ? locations.map((l) => l.name).join(", ")
      : null;

  return (
    <Link
      href={`/tournaments/${tournament.slug}`}
      className="tc"
      aria-label={tournament.name}
    >
      {/* Corner ornaments */}
      <svg className="tc-corner tl" aria-hidden="true">
        <use href="#tc-cn" />
      </svg>
      <svg className="tc-corner tr" aria-hidden="true">
        <use href="#tc-cn" />
      </svg>
      <svg className="tc-corner bl" aria-hidden="true">
        <use href="#tc-cn" />
      </svg>
      <svg className="tc-corner br" aria-hidden="true">
        <use href="#tc-cn" />
      </svg>

      {/* Title banner (hex) */}
      <div className="tc-name line-clamp-2">{tournament.name}</div>

      {/* Hero (image or emblem) with floating status chip */}
      <div className="tc-hero">
        {tournament.headerImage ? (
          <div className="tc-hero-img">
            <Image
              src={tournament.headerImage}
              alt=""
              fill
              sizes="(max-width: 768px) 100vw, 400px"
            />
          </div>
        ) : (
          <div className="tc-hero-emblem">
            <span className="tc-hero-rays" aria-hidden="true" />
            <Icon
              icon="game-icons:stone-tower"
              className="tc-hero-tower"
              aria-hidden="true"
            />
          </div>
        )}

        {status && (
          <span className={`tc-status tc-status-${status.variant}`}>
            <span className="truncate">{status.label}</span>
          </span>
        )}
      </div>

      {/* Description (flavor text) */}
      {tournament.description && (
        <div className="tc-desc">
          <span className="tc-desc-text">{tournament.description}</span>
        </div>
      )}

      {/* Meta rows (2 columns) */}
      <div className="tc-meta">
        <div className="tc-meta-row">
          <Icon
            icon={getSportIcon(tournament.sport)}
            className="tc-meta-icon"
            aria-hidden="true"
          />
          <span>{t(`sports.${tournament.sport}`)}</span>
        </div>

        <div className="tc-meta-row">
          <Icon
            icon="game-icons:calendar"
            className="tc-meta-icon"
            aria-hidden="true"
          />
          <span>
            {formatInTimeZone(tournament.startsAt, "Europe/Helsinki", "d.M.")} –{" "}
            {formatInTimeZone(
              tournament.endsAt,
              "Europe/Helsinki",
              "d.M.yyyy",
            )}
          </span>
        </div>

        <div className="tc-meta-row">
          <Icon
            icon="game-icons:fairy-wand"
            className="tc-meta-icon"
            aria-hidden="true"
          />
          <span>{organizer.name}</span>
        </div>

        {locationLabel && (
          <div className="tc-meta-row">
            <Icon
              icon="game-icons:position-marker"
              className="tc-meta-icon"
              aria-hidden="true"
            />
            <span>{locationLabel}</span>
          </div>
        )}
      </div>

      {/* Divider with tower emblem */}
      <div className="tc-divider">
        <svg className="tc-divider-emblem" aria-hidden="true">
          <use href="#tc-tower-sm" />
        </svg>
      </div>

      {/* Stats (3 columns with vertical separators) */}
      <div className="tc-stats">
        <div className="tc-stat">
          <span className="tc-stat-num">{teamCount}</span>
          <span className="tc-stat-label">
            {t("statistics.teams", { count: teamCount })}
          </span>
        </div>

        <div className="tc-stat">
          <span className="tc-stat-num">{playerCount}</span>
          <span className="tc-stat-label">
            {t("statistics.players", { count: playerCount })}
          </span>
        </div>

        <div className="tc-stat">
          <span className="tc-stat-num">{freePlayerCount}</span>
          <span className="tc-stat-label">
            {t("statistics.freePlayers", { count: freePlayerCount })}
          </span>
        </div>
      </div>
    </Link>
  );
}
